import { CertificateDto } from '@/services/api/tenant/certificates'

export type CertificateYearGroup = {
  year: number
  certificates: CertificateDto[]
}

// `obtained_on` is a date-only string, read as text so no timezone can move a certificate into
// the year either side. A date that cannot be read falls to the current year, where the learner
// can still reach the certificate.
const issuedYear = (certificate: CertificateDto, fallback: number): number => {
  const year = Number(certificate.obtained_on?.slice(0, 4))
  return year > 0 ? year : fallback
}

/**
 * The page's year sections, newest year first: every year from the later of the current year and
 * the newest certificate down to the oldest certificate, with no gaps, so a year that earned
 * nothing still gets a section. Inside a year the newest certificate comes first.
 */
export const groupCertificatesByYear = (
  certificates: CertificateDto[],
  currentYear: number,
): CertificateYearGroup[] => {
  if (certificates.length === 0) {
    return []
  }

  const newestFirst = [...certificates].sort((a, b) => b.obtained_on.localeCompare(a.obtained_on))
  const byYear = new Map<number, CertificateDto[]>()
  newestFirst.forEach((certificate) => {
    const year = issuedYear(certificate, currentYear)
    byYear.set(year, [...(byYear.get(year) ?? []), certificate])
  })

  const issuedYears = [...byYear.keys()]
  const newest = Math.max(currentYear, ...issuedYears)
  const oldest = Math.min(currentYear, ...issuedYears)

  const groups: CertificateYearGroup[] = []
  for (let year = newest; year >= oldest; year -= 1) {
    groups.push({ year, certificates: byYear.get(year) ?? [] })
  }
  return groups
}
